import TableCommonHook from '@op/components/TableCommonHook';
import { useSummaryListColumns } from '@op/components/TableCommonHook/hooks/useSummaryListColumns';
import ReportAccessPanel from '@op/components/ReportPermission';
import { useOpenReportPermission } from '@op/hooks/useOpenReportPermission';
import {
  useDownloadSummaryItem,
  usePreviewSummaryItem,
  RecordsType,
} from '@op/pages/AIAnalysisHub/hooks';
import type { TReportOwner } from '@op/components/ReportPermission';
import { DBO_GAME } from '@op/utils/format';
import React, { useCallback } from 'react';
import { TabType, useAiTaskList } from '../../hooks';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

export default function SummaryListTable({
  filter,
  source,
  keys,
  gameInfoMap,
  deleteToggleState,
  urlTab,
}: {
  filter: any;
  source: TabType;
  keys: string[];
  gameInfoMap?: { [key: string]: any; };
  deleteToggleState: () => void;
  urlTab?: TabType;
}) {
  const {
    pageInfo,
    loading,
    sortBy,
    setSortBy,
    changePageInfo,
    data,
    total,
    deleteMap,
    refreshList,
  } = useAiTaskList({
    filter,
    source,
  });

  const handlePermissionSaved = useCallback(() => {
    refreshList();
    deleteToggleState();
  }, [deleteToggleState, refreshList]);

  const { open: openPermissionPanel, openByTarget, visible, target, close } =
    useOpenReportPermission();

  const downloadSummaryMap = useDownloadSummaryItem();
  const previewSummaryMap = usePreviewSummaryItem();

  const { columns, sortColumns } = useSummaryListColumns({
    clickDelete: async (records: RecordsType) => {
      buriedBtnPoint('Delete', source);
      const key = records.category;
      const gameInfo = gameInfoMap?.[records.game_id];
      await deleteMap[key]?.({ ...records, ...DBO_GAME.params(gameInfo) });
      deleteToggleState();
    },
    clickPreview: (records: RecordsType) => {
      const key = records.category;
      const owner = filter.owner as TReportOwner;
      if (['keyword', 'topic', 'keyword_topic'].includes(key)) {
        (
          previewSummaryMap[key] as (
            record: RecordsType,
            tab?: TabType,
            source?: TabType,
            owner?: TReportOwner,
          ) => void
        )?.(records, urlTab, source, owner);
        return;
      }
      (
        previewSummaryMap[key] as (
          record: RecordsType,
          tab?: TabType,
          owner?: TReportOwner,
        ) => void
      )?.(records, urlTab, owner);
    },
    clickDownload: (records: RecordsType) => {
      const key = records.category;
      const gameInfo = gameInfoMap?.[records.game_id];
      if (!gameInfo) return;
      const params = {
        ...records,
        gameInfo: DBO_GAME.params(gameInfo),
      };
      const owner = filter.owner;
      if (['keyword', 'topic', 'keyword_topic', 'url'].includes(key)) {
        downloadSummaryMap[key]?.(params, urlTab, owner);
        return;
      }
      downloadSummaryMap[key]?.(params);
    },
    keys,
    toggleModal: () => { },
    noNewTagsReport: undefined,
    newTagLoading: undefined,
    gameInfoMap,
    onOpenPermissionPanel: openPermissionPanel,
  });

  return (
    <>
      <TableCommonHook
        columns={columns}
        sortColumns={sortColumns}
        records={data}
        total={total}
        loading={loading}
        pageInfo={pageInfo}
        height={506}
        setPageInfo={changePageInfo}
        sortInfo={[sortBy, setSortBy]}
      ></TableCommonHook>
      <ReportAccessPanel
        visible={visible}
        target={target}
        onClose={close}
        onSaved={handlePermissionSaved}
        notificationMode="list"
        onPermissionLinkClick={openByTarget}
      />
    </>
  );
}
